import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutGrid,
  Activity,
  TrendingUp,
  Package,
  Wallet,
  MessageCircle,
  Settings,
  Menu,
  Shield,
  Users,
  FolderPlus,
} from "lucide-react";
import softindexLogo from "../../assets/images/softindex-logo.webp";
import { useAuth } from "../../context/authContext";
import styles from "./PortalSidebar.module.css";

const iconProps = {
  "aria-hidden": true,
  strokeWidth: 2,
  size: 22,
};

const navItemClass = ({ isActive }) =>
  `${styles.navItem} ${isActive ? styles.navItemActive : ""}`;

export default function PortalSidebar({
  open,
  onNavigate,
  isDesktop = false,
  desktopVisible = true,
  onCollapseDesktop = () => {},
}) {
  const { t } = useTranslation(["common", "dashboard"]);
  const { user } = useAuth();
  const isAdminOrOwner =
    user?.role === "admin" || user?.role === "owner";

  const closeMobile = () => onNavigate?.();

  const handleHeadMenuClick = () => {
    if (isDesktop) {
      onCollapseDesktop();
    } else {
      closeMobile();
    }
  };

  return (
    <aside
      className={`${styles.sidebar} ${!isDesktop && open ? styles.sidebarOpen : ""} ${
        isDesktop && !desktopVisible ? styles.sidebarDesktopHidden : ""
      }`}
      aria-label="Portal navigation"
      aria-hidden={isDesktop && !desktopVisible ? true : undefined}
    >
      <div className={styles.sidebarHead}>
        <div className={styles.sidebarHeadBar}>
          <button
            type="button"
            className={styles.headMenuBtn}
            aria-label={
              isDesktop ? t("sidebar.collapseSidebar") : t("sidebar.closeMenu")
            }
            onClick={handleHeadMenuClick}
          >
            <Menu size={22} strokeWidth={2} aria-hidden />
          </button>
          <div className={styles.brand}>
            <img
              src={softindexLogo}
              alt=""
              className={styles.brandLogo}
              width={120}
              height={32}
              decoding="async"
            />
            <span className={styles.brandTitle}>{t("sidebar.brandTitle")}</span>
          </div>
        </div>
      </div>

      <nav className={styles.nav}>
        <p className={styles.navSectionLabel}>{t("sidebar.sectionMain")}</p>
        <NavLink
          to={isAdminOrOwner ? "/admin" : "/"}
          end
          className={navItemClass}
          onClick={closeMobile}
        >
          {isAdminOrOwner ? (
            <Shield {...iconProps} className={styles.navIcon} />
          ) : (
            <LayoutGrid {...iconProps} className={styles.navIcon} />
          )}
          <span>
            {isAdminOrOwner ? t("sidebar.adminHome") : t("portalNav")}
          </span>
        </NavLink>
        <NavLink
          to="/traffic"
          className={navItemClass}
          onClick={closeMobile}
        >
          <Activity {...iconProps} className={styles.navIcon} />
          <span>{t("trafficNav")}</span>
        </NavLink>
        <NavLink
          to="/seo"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
          }
          onClick={closeMobile}
        >
          <TrendingUp {...iconProps} className={styles.navIcon} />
          <span>{t("seoNav")}</span>
        </NavLink>

        {isAdminOrOwner ? (
          <>
            <p className={styles.navSectionLabel}>
              {t("sidebar.sectionAdmin")}
            </p>
            <NavLink
              to="/user-info"
              className={navItemClass}
              onClick={closeMobile}
            >
              <Users {...iconProps} className={styles.navIcon} />
              <span>{t("userInfo")}</span>
            </NavLink>
            <NavLink
              to="/create-project"
              className={navItemClass}
              onClick={closeMobile}
            >
              <FolderPlus {...iconProps} className={styles.navIcon} />
              <span>{t("createProject")}</span>
            </NavLink>
          </>
        ) : null}

        <p className={styles.navSectionLabel}>{t("sidebar.sectionServices")}</p>
        <NavLink
          to="/products"
          className={navItemClass}
          onClick={closeMobile}
        >
          <Package {...iconProps} className={styles.navIcon} />
          <span>{t("products")}</span>
        </NavLink>
        <NavLink
          to="/billing"
          className={navItemClass}
          onClick={closeMobile}
        >
          <Wallet {...iconProps} className={styles.navIcon} />
          <span>{t("billing")}</span>
        </NavLink>
        <NavLink
          to="/support"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
          }
          onClick={closeMobile}
        >
          <MessageCircle {...iconProps} className={styles.navIcon} />
          <span>{t("supportNav")}</span>
        </NavLink>
        <NavLink
          to="/settings"
          className={navItemClass}
          onClick={closeMobile}
        >
          <Settings {...iconProps} className={styles.navIcon} />
          <span>{t("settings.title")}</span>
        </NavLink>
      </nav>
    </aside>
  );
}
